import type { RouteLocationRaw } from 'vue-router';
import { useSessionBroadcast } from '@/shared/common/composables/useSessionBroadcast';
import { authService } from '@/shared/common/services/auth.service';
import { clearAuthenticationHeaders } from '@/api/instances/axiosRegistry';
import { closeAllWebSockets } from '@/composables/useWebSocket';
import { activeStores } from '@/stores';
import { AUTH_TOKEN_KEY } from '@/shared/common/constants/auth.constant';
import { logger } from '@/shared/common/utils/logger.util';
import type { LogOutOptions } from '@/types/AuthType';

export const session = (): string | null => {
  return localStorage.getItem(AUTH_TOKEN_KEY);
};

export const clearLogin = (): void => {
  localStorage.clear();
};

/**
 * Limpia la data temporal usada en el flujo de sign-up.
 * Esta data se guarda con la key literal `data` en `localStorage`
 * y no debe persistir entre cargas de la web o secciones anteriores.
 */
export const clearSignUpData = (): void => {
  localStorage.removeItem('data');
};

/**
 * Limpia el estado de todos los stores instanciados en la sesion.
 * Los stores con sintaxis setup que no exponen `$reset` propio igual reciben
 * uno de Pinia: lanza en dev-server y en tests, y es un `noop` en los builds.
 * Por eso la falla se atrapa con `try/catch` y no verificando el metodo, que
 * siempre existe. El barrido continua con los stores restantes y el store
 * ofensor queda reportado en lugar de cortar el logout.
 */
const resetAllStores = (): void => {
  activeStores.forEach((store) => {
    try {
      store.$reset();
    } catch (error) {
      logger.error(
        'Store sin $reset: su estado no se limpio durante el logout',
        { storeId: store.$id },
        error as Error
      );
    }
  });
};

/** Limpia la sesion local sin avisar al backend: la usan el logout y las pestañas que reciben un cierre. */
export function clearLocalSession(): void {
  clearLogin();
  clearAuthenticationHeaders();
  resetAllStores();
  closeAllWebSockets();
}

/** Ventana en ms durante la cual se ignoran logouts concurrentes. */
const LOGOUT_REENTRY_DELAY_MS = 1000;

// Flag to prevent multiple logout calls
let isLoggingOut = false;

/**
 * Cierra la sesion: notifica al backend y limpia todo el estado local.
 * La limpieza local vive en el `finally` a proposito: pase lo que pase con la
 * peticion, la sesion local debe quedar limpia. La liberacion de `isLoggingOut`
 * se agenda primero, antes de la limpieza, porque si algo del bloque falla el
 * flag quedaria en `true` y el guard de reentrada convertiria cada reintento en
 * un no-op silencioso hasta que el usuario recargue la pagina.
 */
export const logOut = async (options: LogOutOptions = {}): Promise<void> => {
  const { forceReload = false, silent = false } = options;
  if (isLoggingOut) return;

  isLoggingOut = true;

  const { notifyLogOutAllTabs, notifySessionChange } = useSessionBroadcast();

  try {
    await authService.logout();
  } finally {
    setTimeout(() => {
      isLoggingOut = false;
    }, LOGOUT_REENTRY_DELAY_MS);

    clearLocalSession();

    if (silent) {
      notifySessionChange();
    } else {
      notifyLogOutAllTabs();
    }

    if (forceReload) {
      location.reload();
    }
  }
};

export const maintenance = (): RouteLocationRaw => {
  clearLogin();
  return { name: 'maintenance' };
};

/* eslint-disable no-useless-escape */
export const getWebDeviceInfo = (): {
  device: string;
  device_os: string;
  device_os_version: string;
} => {
  try {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any)?.opera;
    let device = 'desktop';
    if (/android/i.test(userAgent) || /iPad|iPhone|iPod/.test(userAgent)) {
      device = 'mobile';
    }

    let device_os = 'default_os';
    let device_os_version = 'default_version';

    if (/android/i.test(userAgent)) {
      device_os = 'Android';
      const match = userAgent.match(/Android\s([0-9\.]+)/);
      if (match && match[1]) {
        device_os_version = match[1];
      }
    } else if (/iPad|iPhone|iPod/.test(userAgent)) {
      device_os = 'iOS';
      const match = userAgent.match(/OS\s([0-9\_]+)/);
      if (match && match[1]) {
        device_os_version = match[1].replace(/_/g, '.');
      }
    } else if (/Windows NT/i.test(userAgent)) {
      device_os = 'Windows';
      const match = userAgent.match(/Windows NT\s([0-9\.]+)/);
      if (match && match[1]) {
        device_os_version = match[1];
      }
    } else if (/Mac OS X/i.test(userAgent)) {
      device_os = 'macOS';
      const match = userAgent.match(/Mac OS X\s([0-9\_]+)/);
      if (match && match[1]) {
        device_os_version = match[1].replace(/_/g, '.');
      }
    }

    return {
      device,
      device_os,
      device_os_version,
    };
  } catch (error) {
    logger.error('No se pudo leer la informacion del dispositivo', { source: 'getWebDeviceInfo' }, error as Error);
    return {
      device: 'unknown',
      device_os: 'unknown',
      device_os_version: 'unknown',
    };
  }
};
